/**
 * Leaves a signed-out browser with none of the last user's data (backlog 258).
 *
 * Sign-out clears storage down to a few device settings and reloads the page, so no store,
 * module cache or component state survives it. main.tsx imports this module first, so the
 * boot check below runs before any store reads storage; it imports nothing from the app for
 * the same reason.
 */

/** Written at sign-out and read by the next page load; its value carries the logout reason. */
export const SIGN_OUT_MARKER_KEY = 'pp-signed-out';
/** Present while a user is signed in, so a start that finds the session gone knows to clear. */
export const SIGNED_IN_FLAG_KEY = 'pp-signed-in';
/** Device settings, not a user's data: the only localStorage keys sign-out keeps. */
export const KEPT_LOCAL_STORAGE_KEYS: readonly string[] = ['app_version', 'configPanelExpanded'];

export type SignOutReason = 'paused' | 'deleted';

/** What the clearing walks: a Storage that can enumerate its keys. */
export type KeyedStorage = Pick<
  Storage,
  'length' | 'key' | 'getItem' | 'setItem' | 'removeItem' | 'clear'
>;

const asReason = (value: string | null): SignOutReason | null =>
  value === 'paused' || value === 'deleted' ? value : null;

export function clearUserStorage(
  local: KeyedStorage = localStorage,
  session: KeyedStorage = sessionStorage,
): void {
  for (let i = local.length - 1; i >= 0; i--) {
    const key = local.key(i);
    if (key !== null && !KEPT_LOCAL_STORAGE_KEYS.includes(key)) local.removeItem(key);
  }
  session.clear();
}

export function reloadToRoot(): void {
  window.location.replace('/');
}

export function signOutClient(
  reason: SignOutReason | null,
  navigate: (url: string) => void = reloadToRoot,
  local: KeyedStorage = localStorage,
  session: KeyedStorage = sessionStorage,
): void {
  clearUserStorage(local, session);
  local.setItem(SIGN_OUT_MARKER_KEY, reason ?? '');
  navigate('/');
}

let bootReason: SignOutReason | null = null;

/** Clears again after a sign-out reload, catching anything written while the old page unloaded. */
export function runBootCleanup(
  local: KeyedStorage = localStorage,
  session: KeyedStorage = sessionStorage,
): void {
  const marker = local.getItem(SIGN_OUT_MARKER_KEY);
  if (marker === null) return;
  bootReason = asReason(marker);
  clearUserStorage(local, session);
}

/** The logout reason the sign-out carried across its reload, once. */
export function takeSignOutReason(): SignOutReason | null {
  const reason = bootReason;
  bootReason = null;
  return reason;
}

if (typeof window !== 'undefined') runBootCleanup();
